import React, { useState, useEffect } from 'react';
import PrimaryButton from '../../Common/PrimaryButton';

/**
 * Formulário para criar ou editar histórico médico (doenças, cirurgias, hospitalizações)
 */
const HistoricoMedicoForm = ({ onClose, onSubmit, initialData = {}, tipo }) => {
    const isEditing = !!initialData.id;
    const [formData, setFormData] = useState({
        tipo: tipo || 'doenca',
        descricao: '',
        data_ocorrencia: '',
        observacoes: '',
        local_cirurgia: '',
        medico_responsavel: '',
        data_internacao: '',
        data_alta: '',
        motivo_internacao: '',
        diagnostico_alta: ''
    });
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isEditing && initialData) {
            setFormData({
                tipo: initialData.tipo || tipo || 'doenca',
                descricao: initialData.descricao || '',
                data_ocorrencia: initialData.data_ocorrencia 
                    ? new Date(initialData.data_ocorrencia).toISOString().substring(0, 10) 
                    : '',
                observacoes: initialData.observacoes || '',
                local_cirurgia: initialData.local_cirurgia || '',
                medico_responsavel: initialData.medico_responsavel || '',
                data_internacao: initialData.data_internacao 
                    ? new Date(initialData.data_internacao).toISOString().substring(0, 10) 
                    : '',
                data_alta: initialData.data_alta 
                    ? new Date(initialData.data_alta).toISOString().substring(0, 10) 
                    : '',
                motivo_internacao: initialData.motivo_internacao || '',
                diagnostico_alta: initialData.diagnostico_alta || ''
            });
        } else {
            setFormData({
                tipo: tipo || 'doenca',
                descricao: '',
                data_ocorrencia: '',
                observacoes: '',
                local_cirurgia: '',
                medico_responsavel: '',
                data_internacao: '',
                data_alta: '',
                motivo_internacao: '',
                diagnostico_alta: ''
            });
        }
        setFormError(null);
    }, [initialData, isEditing, tipo]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);

        // Validação
        if (!formData.descricao.trim()) {
            setFormError('A descrição é obrigatória.');
            return;
        }

        if (formData.tipo === 'hospitalizacao') {
            if (formData.data_internacao && formData.data_alta) {
                const internacao = new Date(formData.data_internacao);
                const alta = new Date(formData.data_alta);
                if (alta < internacao) {
                    setFormError('A data de alta não pode ser anterior à data de internação.');
                    return;
                }
            }
        }

        setIsSubmitting(true);
        try {
            await onSubmit(formData);
            onClose();
        } catch (error) {
            setFormError(error?.response?.data?.message || error?.message || 'Erro ao salvar registro.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm" role="alert">
                    {formError}
                </div>
            )}

            <div>
                <label htmlFor="tipo" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo <span className="text-red-500">*</span>
                </label>
                <select
                    id="tipo"
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    required
                >
                    <option value="doenca">Doença Prévia</option>
                    <option value="cirurgia">Cirurgia</option>
                    <option value="hospitalizacao">Hospitalização</option>
                </select>
            </div>

            <div>
                <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição <span className="text-red-500">*</span>
                </label>
                <textarea
                    id="descricao"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    placeholder="Descreva a doença, cirurgia ou hospitalização..."
                />
            </div>

            <div>
                <label htmlFor="data_ocorrencia" className="block text-sm font-medium text-gray-700 mb-1">
                    Data da Ocorrência
                </label>
                <input
                    type="date"
                    id="data_ocorrencia"
                    name="data_ocorrencia"
                    value={formData.data_ocorrencia}
                    onChange={handleChange}
                    max={new Date().toISOString().substring(0, 10)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                />
            </div>

            {/* Campos específicos para cirurgias */}
            {formData.tipo === 'cirurgia' && (
                <>
                    <div>
                        <label htmlFor="local_cirurgia" className="block text-sm font-medium text-gray-700 mb-1">
                            Local da Cirurgia
                        </label>
                        <input
                            type="text"
                            id="local_cirurgia"
                            name="local_cirurgia"
                            value={formData.local_cirurgia}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="Ex: Hospital X, Clínica Y..."
                        />
                    </div>

                    <div>
                        <label htmlFor="medico_responsavel" className="block text-sm font-medium text-gray-700 mb-1">
                            Médico Responsável
                        </label>
                        <input
                            type="text"
                            id="medico_responsavel"
                            name="medico_responsavel"
                            value={formData.medico_responsavel}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="Nome do médico..."
                        />
                    </div>
                </>
            )}

            {/* Campos específicos para hospitalizações */}
            {formData.tipo === 'hospitalizacao' && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="data_internacao" className="block text-sm font-medium text-gray-700 mb-1">
                                Data de Internação
                            </label>
                            <input
                                type="date"
                                id="data_internacao"
                                name="data_internacao"
                                value={formData.data_internacao}
                                onChange={handleChange}
                                max={new Date().toISOString().substring(0, 10)}
                                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="data_alta" className="block text-sm font-medium text-gray-700 mb-1">
                                Data de Alta
                            </label>
                            <input
                                type="date"
                                id="data_alta"
                                name="data_alta"
                                value={formData.data_alta}
                                onChange={handleChange}
                                max={new Date().toISOString().substring(0, 10)}
                                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="motivo_internacao" className="block text-sm font-medium text-gray-700 mb-1">
                            Motivo da Internação
                        </label>
                        <textarea
                            id="motivo_internacao"
                            name="motivo_internacao"
                            value={formData.motivo_internacao}
                            onChange={handleChange}
                            rows={2}
                            className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="Motivo da internação..."
                        />
                    </div>

                    <div>
                        <label htmlFor="diagnostico_alta" className="block text-sm font-medium text-gray-700 mb-1">
                            Diagnóstico na Alta
                        </label>
                        <textarea
                            id="diagnostico_alta"
                            name="diagnostico_alta"
                            value={formData.diagnostico_alta}
                            onChange={handleChange}
                            rows={2}
                            className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="Diagnóstico ao receber alta..."
                        />
                    </div>
                </>
            )}

            <div>
                <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                </label>
                <textarea
                    id="observacoes"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Observações adicionais..."
                />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Salvar')}
                </PrimaryButton>
            </div>
        </form>
    );
};

export default HistoricoMedicoForm;

